import { useEffect, useMemo, useRef, useState } from "react";

export interface TagAutocompleteInputProps {
  activeTags: string[];
  allSuggestions: string[];
  onAddTag: (tag: string) => void;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
  inputStyle?: React.CSSProperties;
  inputClassName?: string;
  compact?: boolean;
}

export default function TagAutocompleteInput({
  activeTags,
  allSuggestions,
  onAddTag,
  placeholder = "Add tag (press Enter)",
  className = "",
  style,
  inputStyle,
  inputClassName = "",
  compact = false,
}: TagAutocompleteInputProps) {
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Filter suggestions: exclude already active tags, match typed substring
  const filtered = useMemo(() => {
    const trimmed = text.trim().toLowerCase().replace(/^#/, "");
    const remaining = allSuggestions.filter((t) => !activeTags.includes(t));
    if (!trimmed) return remaining;
    return remaining.filter((t) => t.toLowerCase().includes(trimmed));
  }, [allSuggestions, activeTags, text]);

  // Click outside listener: cleanly close suggestions without stealing focus or locking
  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
        setHighlightIdx(-1);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  const commitTag = (rawTag: string) => {
    const cleaned = rawTag.trim().toLowerCase().replace(/^#/, "").replace(/[^a-z0-9_-]/g, "-");
    if (!cleaned) return;
    if (!activeTags.includes(cleaned)) {
      onAddTag(cleaned);
    }
    setText("");
    setHighlightIdx(-1);

    // Retain focus inside the "Add tag" input immediately after a tag is selected or created
    inputRef.current?.focus();
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });

    // Keep dropdown open so the user can select multiple tags consecutively
    setOpen(true);
  };

  return (
    <div
      ref={wrapRef}
      className={`orch-autocomplete-wrap ${className}`}
      style={{ position: "relative", display: compact ? "inline-block" : "block", ...style }}
    >
      <input
        ref={inputRef}
        type="text"
        placeholder={placeholder}
        value={text}
        className={inputClassName}
        style={inputStyle}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
          setHighlightIdx(-1);
        }}
        onFocus={() => {
          setOpen(true);
        }}
        onClick={() => {
          setOpen(true);
        }}
        onMouseDown={() => {
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            if (!open) {
              setOpen(true);
            } else if (filtered.length > 0) {
              setHighlightIdx((prev) => (prev + 1) % filtered.length);
            }
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            if (open && filtered.length > 0) {
              setHighlightIdx((prev) => (prev <= 0 ? filtered.length - 1 : prev - 1));
            }
          } else if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            if (open && highlightIdx >= 0 && highlightIdx < filtered.length) {
              commitTag(filtered[highlightIdx]);
            } else if (text.trim()) {
              commitTag(text);
            }
          } else if (e.key === "Escape") {
            setOpen(false);
            setHighlightIdx(-1);
          }
        }}
      />

      {open && filtered.length > 0 && (
        <div
          className={`orch-autocomplete-menu ${compact ? "compact" : ""}`}
          role="listbox"
        >
          {filtered.map((tag, idx) => (
            <div
              key={tag}
              className={`orch-autocomplete-item ${idx === highlightIdx ? "highlighted" : ""} ${
                compact ? "compact" : ""
              }`}
              role="option"
              aria-selected={idx === highlightIdx}
              onMouseDown={(e) => {
                // Prevent mousedown from blurring the input
                e.preventDefault();
                commitTag(tag);
              }}
              onMouseEnter={() => setHighlightIdx(idx)}
            >
              <span style={{ color: "var(--accent)", fontWeight: 600 }}>#</span>
              <span>{tag}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
